/**
 * App shell — migrated to @selerity/ui AppShell with collapsible sidebar.
 * Replaces the 202-line local implementation with a config-driven wrapper.
 */
import { type ReactNode } from "react";
import { AppShell as SharedAppShell, type NavEntry } from "@selerity/ui";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExtensionIcon from "@mui/icons-material/Extension";
import WorkIcon from "@mui/icons-material/Work";
import ChatIcon from "@mui/icons-material/Chat";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SettingsIcon from "@mui/icons-material/Settings";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureGate } from "@/hooks/useFeatureGate";

declare const __APP_VERSION__: string;

const navigation: NavEntry[] = [
  { label: "Dashboard", path: "/", icon: <DashboardIcon /> },
  { label: "Flows", path: "/plugins", icon: <ExtensionIcon /> },
  { label: "Jobs", path: "/jobs", icon: <WorkIcon /> },
  { label: "Prompts", path: "/prompts", icon: <ChatIcon /> },
  { label: "Schedules", path: "/schedules", icon: <ScheduleIcon /> },
  {
    label: "Admin",
    icon: <AdminPanelSettingsIcon />,
    minRole: "admin",
    items: [
      { label: "Flows", path: "/admin/plugins", icon: <AdminPanelSettingsIcon /> },
      { label: "Credentials", path: "/admin/credentials", icon: <VpnKeyIcon /> },
      { label: "Settings", path: "/admin/settings", icon: <SettingsIcon /> },
    ],
  },
];

export default function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { tier } = useFeatureGate("admin");

  const givenName = (user?.profile?.given_name as string) ?? "";
  const familyName = (user?.profile?.family_name as string) ?? "";
  const displayName = [givenName, familyName].filter(Boolean).join(" ") || user?.profile?.email || "Staff";
  const email = (user?.profile?.email as string) ?? "";

  const roleHierarchy = ["user", "power_user", "admin"];
  const currentRole = tier ?? "user";

  return (
    <SharedAppShell
      appTitle="Reporting Platform"
      navigation={navigation}
      roleHierarchy={roleHierarchy}
      currentRole={currentRole}
      appVersion={__APP_VERSION__}
      userDisplayName={displayName}
      userEmail={email}
      userRoleLabel={currentRole === "admin" ? "Admin" : currentRole === "power_user" ? "Power User" : "User"}
      onSignOut={logout}
      collapsible={true}
    >
      {children}
    </SharedAppShell>
  );
}
